import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiUrl } from "../config/api";

type LoginResponse = {
  ok?: boolean;
  token?: string;
  user?: {
    id: number;
    username: string;
    full_name: string;
    role: string;
  };
  error?: string;
};

const AUTH_LOGIN_API = apiUrl("/auth/login");

export default function LoginPage({
  onLogin,
}: {
  onLogin?: (payload: { token: string; user: NonNullable<LoginResponse["user"]> }) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);
  const [pendingLogin, setPendingLogin] = useState<{
    token: string;
    user: NonNullable<LoginResponse["user"]>;
  } | null>(null);

  useEffect(() => {
    if (redirectCountdown == null) {
      return undefined;
    }

    if (redirectCountdown <= 0) {
      if (pendingLogin) {
        onLogin?.(pendingLogin);
      }
      navigate("/");
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setRedirectCountdown((prev) => (prev == null ? prev : prev - 1));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [redirectCountdown, navigate, onLogin, pendingLogin]);

  // PMN-CRITICAL(/login): Login stores token/user, but sale_channels are loaded later by App via /auth/me.
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const resp = await fetch(AUTH_LOGIN_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          password,
        }),
      });

      const data = (await resp.json().catch(() => ({}))) as LoginResponse;
      if (!resp.ok || !data.ok || !data.token || !data.user) {
        throw new Error(data.error ?? "failed_to_login");
      }

      localStorage.setItem("bg_shop_token", data.token);
      localStorage.setItem("bg_shop_user", JSON.stringify(data.user));
      setPendingLogin({ token: data.token, user: data.user });
      setRedirectCountdown(2);
    } catch (err) {
      const message = err instanceof Error ? err.message : "failed_to_login";
      if (message === "invalid_credentials") {
        setError("ชื่อผู้ใช้หรือรหัสผ่านไม่ถูกต้อง");
      } else if (message === "user_inactive") {
        setError("บัญชีนี้ถูกปิดการใช้งาน");
      } else {
        setError("เข้าสู่ระบบไม่สำเร็จ");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-vh-100 bg-primary d-flex align-items-center">
      <div className="container py-4 py-md-5">
        <div className="row justify-content-center">
          <div className="col-12 col-sm-10 col-md-7 col-lg-5 col-xl-4">
            <div className="card shadow-sm border-0 rounded-4">
              <div className="card-body p-4 p-md-5 bg-white">
                <div className="text-center mb-4">
                  <h3 className="text-primary mb-2">เข้าสู่ระบบ</h3>
                  <div className="small text-muted">
                    ใช้บัญชีของคุณเพื่อเข้าใช้งานระบบ bg.shop
                  </div>
                </div>

                {error && <div className="alert alert-danger rounded-3">{error}</div>}

                {redirectCountdown != null && (
                  <div className="alert alert-success rounded-3 text-center">
                    <div className="fw-semibold mb-1">Login successful</div>
                    <div className="small">
                      Redirecting to the main menu in {Math.max(redirectCountdown, 0)} seconds...
                    </div>
                  </div>
                )}

                <form className="d-grid gap-3" onSubmit={(e) => void handleSubmit(e)}>
                  <div>
                    <label className="form-label">ชื่อผู้ใช้</label>
                    <input
                      type="text"
                      className="form-control"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                      placeholder="กรอกชื่อผู้ใช้"
                    />
                  </div>

                  <div>
                    <label className="form-label">รหัสผ่าน</label>
                    <input
                      type="password"
                      className="form-control"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      placeholder="กรอกรหัสผ่าน"
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting || redirectCountdown != null}
                  >
                    {submitting ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ"}
                  </button>
                </form>

                <div className="text-center mt-4">
                  <Link to="/" className="btn btn-link text-decoration-none">
                    กลับหน้าเมนู
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
